import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { type ReactNode, useState } from 'react';
import type { DetailCreative } from '../../data/api.js';
import type { CreativeDetailScope, DeliveryNode } from '../data/api.js';
import { SectionHeader } from '../shared/SectionHeader.js';
import { CreativeSummary } from './CreativeSummary.js';
import { DeliverySection } from './DeliverySection.js';
import { PerformanceTiles } from './PerformanceTiles.js';
import { SpendBreakdownSection } from './SpendBreakdownSection.js';
import { TrendSection } from './TrendSection.js';

interface OverviewTabProps {
  scope: CreativeDetailScope;
  creative: DetailCreative;
  kpis: string[];
  leadingKpiLabel: string;
  onSeeBreakdown: () => void;
}

export function OverviewTab({
  scope,
  creative,
  kpis,
  leadingKpiLabel,
  onSeeBreakdown,
}: OverviewTabProps): ReactNode {
  const { creativeId } = creative;
  const [node, setNode] = useState<DeliveryNode | undefined>(undefined);
  const [metricNames, setMetricNames] = useState<[string] | [string, string]>(['ctr', 'cost']);
  const selectTrendMetric = (metricName: string): void =>
    setMetricNames((current) => {
      if (current.includes(metricName)) {
        const remaining = current.find((name) => name !== metricName);
        return remaining === undefined ? current : [remaining];
      }
      return current.length === 2 ? [metricName, current[1]] : [metricName, current[0]];
    });

  return (
    <Stack sx={{ gap: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PerformanceTiles
            scope={scope}
            creativeId={creativeId}
            kpis={kpis}
            selectedMetricNames={metricNames}
            onMetricSelect={selectTrendMetric}
          />
        </Box>
        <CreativeSummary
          creative={creative}
          scope={scope}
          leadingKpiLabel={leadingKpiLabel}
          onSeeBreakdown={onSeeBreakdown}
        />
      </Box>

      <Divider />

      <TrendSection
        scope={scope}
        creativeId={creativeId}
        node={node}
        metricNames={metricNames}
        availableMetrics={kpis}
        onMetricNamesChange={setMetricNames}
      />

      <Stack sx={{ gap: 3 }}>
        <SectionHeader
          title="Where the spend went"
          caption="This creative split by breakdown dimension"
        />
        <SpendBreakdownSection scope={scope} creativeId={creativeId} kpis={kpis} />
      </Stack>

      <DeliverySection scope={scope} creativeId={creativeId} kpis={kpis} onNodeSelect={setNode} />
    </Stack>
  );
}
